// ./src/components/features/Support/SupportDrawer/SupportDrawer.tsx
import axios from "axios";
import { appEnv } from "@/config/branding";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Stack,
  Textarea,
  TextInput,
  Text,
  Group,
  Box,
  Image,
  ActionIcon,
  Button,
  SimpleGrid,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useApp } from "@/contexts";
import { AppDrawer, AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface SupportDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Pre-select a topic (e.g. "bug_report" when reporting an error). */
  initialTopic?: string;

  /** Pre-fill the message (e.g. the error details + where it happened). */
  initialMessage?: string;
}

// ============================================
// Interfaces
// ============================================

interface AttachedFile {
  id: string;
  file: File;
  previewUrl: string | null;
}

interface FormData {
  subject: string; // Honeypot field - must remain empty
  name: string;
  email: string;
  topic: string;
  message: string;
}

// ============================================
// Constants
// ============================================

const SUPPORT_TOPICS = [
  { value: "bug_report", translationKey: "support:topics.bugReport" },
  { value: "feature_request", translationKey: "support:topics.featureRequest" },
  { value: "general_question", translationKey: "support:topics.generalQuestion" },
  { value: "account_billing", translationKey: "support:topics.accountBilling" },
] as const;

// FormSubmits configuration
const FORM_ID = appEnv.formSubmitsFormId;
const FORM_ENDPOINT = `https://api.formsubmits.com/api/submissions/${FORM_ID}`;

const ACCEPTED_FILE_TYPES = "image/*,.pdf,.doc,.docx,.txt";
const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ============================================
// Helper Functions
// ============================================

const getInitialFormData = (
  name: string,
  email: string,
  topic?: string,
  message?: string
): FormData => ({
  subject: "", // Honeypot - must stay empty
  name,
  email,
  topic: topic || SUPPORT_TOPICS[2].value,
  message: message || "",
});

const generateFileId = (): string =>
  `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

const createPreviewUrl = (file: File): string | null => {
  if (file.type.startsWith("image/")) {
    return URL.createObjectURL(file);
  }
  return null;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ============================================
// Dirty-state serializer (stable key order)
// ============================================

const serializeFormSnapshot = (
  data: FormData,
  files: AttachedFile[]
): string => {
  return JSON.stringify({
    ...data,
    _fileIds: files.map((f) => f.id).sort(),
  });
};

// ============================================
// File Thumbnail Component
// ============================================

interface FileThumbnailProps {
  attachedFile: AttachedFile;
  onRemove: (id: string) => void;
}

const FileThumbnail: React.FC<FileThumbnailProps> = ({
  attachedFile,
  onRemove,
}) => {
  const theme = useMantineTheme();
  const { file, previewUrl, id } = attachedFile;
  const isImage = file.type.startsWith("image/");

  return (
    <Box
      pos="relative"
      style={{
        borderRadius: theme.radius.md,
        border: `1px solid ${theme.colors.neutral[2]}`,
        overflow: "hidden",
      }}
    >
      {/* Thumbnail */}
      <Box
        h={80}
        bg="neutral.0"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isImage && previewUrl ? (
          <Image
            src={previewUrl}
            alt={file.name}
            h={80}
            fit="cover"
            style={{ width: "100%" }}
          />
        ) : (
          <AppIcon
            iconName="FileText"
            size={28}
            color={theme.colors.neutral[4]}
          />
        )}
      </Box>

      {/* File info */}
      <Box px="xs" py={4}>
        <Text size="md" c="neutral.7" truncate fw={500} lh={1.3}>
          {file.name}
        </Text>
        <Text size="xs" c="dimmed" lh={1.3}>
          {formatFileSize(file.size)}
        </Text>
      </Box>

      {/* Remove button */}
      <ActionIcon
        size="xs"
        variant="filled"
        color="neutral"
        radius="xl"
        pos="absolute"
        top={4}
        right={4}
        onClick={() => onRemove(id)}
        style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          border: "none",
        }}
      >
        <AppIcon iconName="X" size={10} color="white" />
      </ActionIcon>
    </Box>
  );
};

// ============================================
// Component
// ============================================

export const SupportDrawer: React.FC<SupportDrawerProps> = ({
  opened,
  onClose,
  initialTopic,
  initialMessage,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["support", "common", "validation"]);
  const { user } = useApp();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Form state
  const [formData, setFormData] = useState<FormData>(() =>
    getInitialFormData(
      user?.username || "",
      user?.email || "",
      initialTopic,
      initialMessage
    )
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Attached files from device
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  // Track dirty state reactively
  useEffect(() => {
    if (!opened || submitSuccess) return;
    const current = serializeFormSnapshot(formData, attachedFiles);
    setIsDirty(current !== initialSnapshotRef.current);
  }, [formData, attachedFiles, opened, submitSuccess]);

  // Build translated topic options
  const topicOptions = SUPPORT_TOPICS.map((topic) => ({
    value: topic.value,
    label: t(topic.translationKey),
  }));

  // Cleanup preview URLs on unmount or when files change
  useEffect(() => {
    return () => {
      attachedFiles.forEach((af) => {
        if (af.previewUrl) {
          URL.revokeObjectURL(af.previewUrl);
        }
      });
    };
  }, [attachedFiles]);

  // Reset form when drawer opens
  useEffect(() => {
    if (opened) {
      const newFormData = getInitialFormData(
        user?.username || "",
        user?.email || "",
        initialTopic,
        initialMessage
      );
      const newFiles: AttachedFile[] = [];

      setFormData(newFormData);
      setAttachedFiles(newFiles);

      // Capture initial snapshot for dirty tracking
      initialSnapshotRef.current = serializeFormSnapshot(newFormData, newFiles);
      setIsDirty(false);

      setValidationErrors({});
      setSubmitError(null);
      setSubmitSuccess(false);
      setSuccessMessage("");
    }
  }, [opened, user?.username, user?.email, initialTopic, initialMessage]);

  // ============================================
  // Validation
  // ============================================

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = t("validation:required");
    }

    if (!formData.email.trim()) {
      errors.email = t("validation:required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t("validation:invalidEmail");
    }

    if (!formData.topic) {
      errors.topic = t("validation:required");
    }

    if (!formData.message.trim()) {
      errors.message = t("validation:required");
    } else if (formData.message.trim().length < 10) {
      errors.message = t("support:form.validation.messageTooShort");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  // ============================================
  // Field Handlers
  // ============================================

  const handleFieldChange = useCallback(
    (field: keyof FormData, value: string | null) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear validation error when user changes field
      setValidationErrors((prev) => {
        if (prev[field]) {
          const { [field]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });

      // Clear submit error on any change
      setSubmitError(null);
    },
    []
  );

  const handleTextInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleFieldChange(e.target.name as keyof FormData, e.target.value);
    },
    [handleFieldChange]
  );

  // ============================================
  // File Handlers
  // ============================================

  const handleAddFilesClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const remaining = MAX_FILES - attachedFiles.length;
      if (remaining <= 0) return;

      const newFiles: AttachedFile[] = [];

      for (let i = 0; i < Math.min(files.length, remaining); i++) {
        const file = files[i];

        // Validate file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
          setSubmitError(
            t("support:form.errors.fileTooLarge", {
              name: file.name,
              maxSize: MAX_FILE_SIZE_MB,
            })
          );
          continue;
        }

        newFiles.push({
          id: generateFileId(),
          file,
          previewUrl: createPreviewUrl(file),
        });
      }

      if (newFiles.length > 0) {
        setAttachedFiles((prev) => [...prev, ...newFiles]);
      }

      // Reset the input so the same file can be selected again
      e.target.value = "";
    },
    [attachedFiles.length, t]
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  }, []);

  // ============================================
  // Submit Handler
  // ============================================

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    setValidationErrors({});

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Find translated topic label for the submission


      // Use FormData to support file uploads
      const payload = new window.FormData();
      payload.append("subject", formData.subject); // Honeypot - must be empty
      payload.append("name", formData.name.trim());
      payload.append("email", formData.email.trim());
      payload.append("topic", formData.topic);
      payload.append("message", formData.message.trim());

      // ⚠ #103 (2026-10-07): the files go WITH the message. FormSubmits refuses the whole submission when a file is
      // blocked or the form takes no files — so a send either carries every picked file or fails with the reason; it
      // never arrives without them.
      attachedFiles.forEach((af) => {
        payload.append("attachments", af.file, af.file.name);
      });

      // No Content-Type: the browser writes the multipart boundary. And every status is READ — a refusal arrives as a
      // 4xx whose body carries the reason, which axios would otherwise throw away as a "network error".
      const response = await axios.post(FORM_ENDPOINT, payload, {
        validateStatus: () => true,
      });

      const result = response.data;

      if (result.code === 0) {
        // Success - extract message from response if available
        const serverMessage =
          result.data?.[0]?.message || t("support:form.successMessage");
        setSuccessMessage(serverMessage);
        setSubmitSuccess(true);

        // Reset dirty so AppDrawer won't block the close
        setIsDirty(false);

        // Auto-close after brief delay to show success
        setTimeout(() => {
          onClose();
        }, 2500);
      } else {
        // Handle validation errors from API
        if (result.errors?.length > 0) {
          const fieldErrs: Record<string, string> = {};
          const generalErrors: string[] = [];

          result.errors.forEach((err: any) => {
            if (err.name && err.errors?.length > 0) {
              const fieldName = err.name === "question" ? "topic" : err.name;
              fieldErrs[fieldName] = err.errors.join(". ");
            } else if (err.errors?.length > 0) {
              generalErrors.push(...err.errors);
            }
          });

          if (Object.keys(fieldErrs).length > 0) {
            setValidationErrors(fieldErrs);
          }

          if (generalErrors.length > 0) {
            setSubmitError(generalErrors.join(". "));
          } else if (Object.keys(fieldErrs).length === 0) {
            setSubmitError(t("support:form.errors.validationFailed"));
          }
        } else {
          setSubmitError(t("support:form.errors.submitFailed"));
        }
      }
    } catch (error) {
      console.error("Support form submission error:", error);
      setSubmitError(t("support:form.errors.networkError"));
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, attachedFiles, validateForm, onClose, t]);

  // ============================================
  // Render
  // ============================================

  const canAddMoreFiles = attachedFiles.length < MAX_FILES;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("support:form.title")}
      position="right"
      size="md"
      responsive
      mobileHeightPercent={92}
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={submitSuccess ? null : submitError}
      actionType={submitSuccess ? undefined : "save-cancel"}
      onConfirm={handleSubmit}
      confirmLabel={t("support:form.actions.send")}
      cancelLabel={t("common:buttons.cancel")}
      isDirty={isDirty}
    >
      {submitSuccess ? (
        <Stack align="center" justify="center" py="xl" gap="md">
          <AppIcon
            iconName="Check"
            size={48}
            color={theme.colors.green[6]}
          />
          <Text size="lg" fw={600} c="green.7" ta="center">
            {t("support:form.successTitle")}
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            {successMessage}
          </Text>
        </Stack>
      ) : (
        <Stack gap="md">
          {/* Honeypot field - hidden from users, visible to bots */}
          <TextInput
            name="subject"
            value={formData.subject}
            onChange={(e) => handleFieldChange("subject", e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          />

          {/* Name */}
          <TextInput
            label={t("support:form.fields.name.label")}
            placeholder={t("support:form.fields.name.placeholder")}
            name="name"
            value={formData.name}
            onChange={handleTextInputChange}
            error={validationErrors.name}
            size="md"
            required
            withAsterisk
            readOnly
          />

          {/* Email */}
          <TextInput
            label={t("support:form.fields.email.label")}
            placeholder={t("support:form.fields.email.placeholder")}
            name="email"
            value={formData.email}
            onChange={handleTextInputChange}
            error={validationErrors.email}
            type="email"
            size="md"
            required
            withAsterisk
            readOnly
          />

          {/* Topic */}
          <SelectField
            label={t("support:form.fields.topic.label")}
            placeholder={t("support:form.fields.topic.placeholder")}
            data={topicOptions}
            value={formData.topic}
            onChange={(_name, value) => handleFieldChange("topic", value)}
            error={validationErrors.topic}
            size="md"
            required
            withAsterisk
            allowDeselect={false}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
          />

          {/* Message */}
          <Textarea
            label={t("support:form.fields.message.label")}
            placeholder={t("support:form.fields.message.placeholder")}
            name="message"
            value={formData.message}
            onChange={handleTextInputChange}
            error={validationErrors.message}
            minRows={4}
            maxRows={8}
            autosize
            size="md"
            required
            withAsterisk
          />

          {/* Attachments */}
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Group gap={4}>
                <Text fw={600} size="md">
                  {t("support:form.fields.attachments.label")}
                </Text>
                <Text size="xs" c="dimmed">
                  ({t("common:optional")})
                </Text>
              </Group>
              {attachedFiles.length > 0 && (
                <Text size="xs" c="dimmed">
                  {attachedFiles.length}/{MAX_FILES}
                </Text>
              )}
            </Group>

            {/* File thumbnails grid */}
            {attachedFiles.length > 0 && (
              <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="xs">
                {attachedFiles.map((af) => (
                  <FileThumbnail
                    key={af.id}
                    attachedFile={af}
                    onRemove={handleRemoveFile}
                  />
                ))}
              </SimpleGrid>
            )}

            {/* Add files button */}
            {canAddMoreFiles && (
              <Button
                variant="light"
                color="neutral"
                size="md"
                leftSection={<AppIcon iconName="Upload" size={16} />}
                onClick={handleAddFilesClick}
              >
                {t("support:form.fields.attachments.addButton")}
              </Button>
            )}

            {/* Hidden native file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              multiple
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />

            {/* A file the form service refused (blocked type) — named at the picker, not lost in the footer. */}
            {validationErrors.attachments && (
              <Text size="md" c="red">
                {validationErrors.attachments}
              </Text>
            )}

            {attachedFiles.length === 0 && (
              <Text size="md" c="dimmed">
                {t("support:form.fields.attachments.hint")}
              </Text>
            )}
          </Stack>

        </Stack>
      )}
    </AppDrawer>
  );
};